import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Checkbox, Empty, Form, Input, Modal, Result, Select, Space, Steps, Table, Tag, Typography, message } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LeftOutlined, RightOutlined, SafetyCertificateOutlined, SendOutlined } from '@ant-design/icons';
import { Navigate } from 'react-router-dom';
import './index.css';
import {
  createDeveloperAgent,
  decideAgentReview,
  listAgentReviews,
  listDeveloperAgents,
  testAgentConnectivity,
} from '../../services/agentDeveloperApi';
import { useAuthStore } from '../../stores/authStore';

const { Text, Title, Paragraph } = Typography;

const FIXED_AGENT_ENDPOINTS = {
  health: '/devforge-agent/v1/health',
  message: '/devforge-agent/v1/messages',
};

const stepItems = [
  { title: '基础信息', description: '这是什么智能体' },
  { title: '服务地址', description: '真实服务 URL' },
  { title: '能力权限', description: '能收什么、能回什么' },
  { title: '确认接入', description: '管理员提交' },
];

const inputTypeOptions = [
  { label: '文本 text', value: 'text' },
  { label: '图片 image', value: 'image' },
  { label: '文件 file', value: 'file' },
  { label: 'JSON', value: 'json' },
  { label: '音频 audio', value: 'audio' },
];

const outputTypeOptions = [
  { label: '文本 text', value: 'text' },
  { label: 'HTML 页面', value: 'html' },
  { label: 'JSON 数据', value: 'json' },
  { label: 'Markdown 文档', value: 'markdown' },
  { label: '文件 file', value: 'file' },
  { label: '图片 image', value: 'image' },
];

const permissionOptions = [
  { label: '读取项目上下文', value: 'project:read' },
  { label: '读取知识库', value: 'kb:read' },
  { label: '创建任务', value: 'task:create' },
  { label: '写入成果文件', value: 'artifact:write' },
  { label: '调用外部服务', value: 'external:call' },
];

function statusColor(status?: string) {
  if (status === 'listed' || status === 'approved') return 'green';
  if (status === 'rejected' || status === 'validation_failed' || status === 'failed') return 'red';
  if (status === 'submitted' || status === 'reviewing') return 'orange';
  return 'blue';
}

function splitValues(value?: string) {
  return (value || '').split(/[,，\s]+/).map(item => item.trim()).filter(Boolean);
}

function buildManifest(values: any) {
  return {
    schemaVersion: '1.0',
    name: values.name,
    description: values.description,
    version: values.version,
    inputModes: values.inputTypes || [],
    outputModes: values.outputTypes || [],
    endpoints: {
      health: '/devforge-agent/v1/health',
      message: '/devforge-agent/v1/messages',
    },
    capabilities: [],
    permissions: (values.permissions || []).map((p: string) => ({ code: p, name: p })),
  };
}

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="agent-dev-summary-row">
      <span>{label}</span>
      <strong>{children || '未填写'}</strong>
    </div>
  );
}

export default function AgentDeveloperCenterPage() {
  const { user } = useAuthStore();
  const isAdmin = user?.roles?.includes('admin') || user?.username === 'admin';
  const [current, setCurrent] = useState(0);
  const [agents, setAgents] = useState<any[]>([]);
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [reviewingId, setReviewingId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [testingUrl, setTestingUrl] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  // 监听所有字段变化，存到 state，避免 useWatch 不触发的 bug
  const handleFormChange = useCallback((_changedValues: any, allValues: any) => {
    const v = { ...allValues };
    window.__agent_dev_form = v;
    setFormValues(v);
  }, []);

  // mount 后从 initialValues 同步一次（onValuesChange 不会在 mount 时触发）
  useEffect(() => {
    const all = form.getFieldsValue(true);
    const v = { ...all };
    console.log('[AGENT-DEV] init formValues:', JSON.stringify(v));
    window.__agent_dev_form = v;
    setFormValues(v);
  }, [form]);

  // 每次 current 变化时，也强行同步一次 form 值，确保切换步骤时数据不丢失
  useEffect(() => {
    const all = form.getFieldsValue(true);
    const v = { ...all };
    console.log('[AGENT-DEV] step=' + current + ' formValues:', JSON.stringify(v));
    window.__agent_dev_form = v;
    setFormValues(v);
  }, [current, form]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [agentRows, reviewRows] = await Promise.all([
        listDeveloperAgents(),
        listAgentReviews(),
      ]);
      setAgents(agentRows);
      setReviews(reviewRows);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewDecision = async (
    review: any,
    action: 'approve' | 'reject' | 'list',
  ) => {
    const execute = async () => {
      setReviewingId(review.id);
      try {
        await decideAgentReview(
          review.id,
          action,
          action === 'reject' ? '管理员驳回智能体接入申请' : undefined,
        );
        message.success(action === 'list' ? '智能体已上架' : action === 'reject' ? '申请已驳回' : '当前审核环节已通过');
        await refresh();
      } catch (error: any) {
        console.error('审核智能体失败:', error.response?.status, error.response?.data?.message || error.message);
        message.error(error.response?.data?.message || '审核操作失败');
      } finally {
        setReviewingId('');
      }
    };

    if (action === 'reject') {
      Modal.confirm({
        title: '确认驳回这条接入申请？',
        content: '申请将退回工坊项目，需修正后重新提交。',
        okText: '确认驳回',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: execute,
      });
      return;
    }
    await execute();
  };

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin]);

  const manifest = useMemo(() => buildManifest(formValues), [formValues]);

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const validateCurrentStep = async () => {
    const fieldsByStep = [
      ['name', 'category', 'version', 'description'],
      ['serviceBaseUrl'],
      ['inputTypes', 'outputTypes'],
      [],
    ];
    await form.validateFields(fieldsByStep[current]);
  };

  const next = async () => {
    await validateCurrentStep();
    setCurrent(current + 1);
  };

  const submit = async () => {
    await form.validateFields();
    const allValues = form.getFieldsValue(true);
    setSubmitting(true);
    try {
      const manifestSnapshot = buildManifest(allValues);
      const payload = {
        name: allValues.name,
        description: allValues.description,
        category: allValues.category,
        subCategory: allValues.subCategory,
        version: allValues.version,
        pricingMode: allValues.pricingMode,
        serviceBaseUrl: allValues.serviceBaseUrl,
        serviceToken: allValues.serviceToken,
        tags: splitValues(allValues.tags),
        capabilities: manifestSnapshot.capabilities,
        manifestSnapshot,
        developerName: '平台管理员',
      };
      console.log('[AGENT-DEV] submitting payload:', JSON.stringify(payload, null, 2));
      await createDeveloperAgent(payload);
      message.success('管理员已提交接入，后台已自动生成智能体编码并直接上架');
      form.resetFields();
      setCurrent(0);
      refresh();
    } catch (err: any) {
      const respData = err?.response?.data;
      console.error('[AGENT-DEV] submit failed', {
        status: err?.response?.status,
        data: respData,
        dataStr: JSON.stringify(respData, null, 2),
        message: err?.message,
      });
      const msg = typeof respData === 'object' && respData !== null
        ? (respData.message || JSON.stringify(respData))
        : (String(respData) || err?.message || '接入失败');
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTestConnectivity = async () => {
    const serviceBaseUrl = form.getFieldValue('serviceBaseUrl');
    if (!serviceBaseUrl || !serviceBaseUrl.trim()) {
      message.warning('请先填写服务地址');
      return;
    }
    setTestingUrl(true);
    setTestResult(null);
    try {
      const res = await testAgentConnectivity(serviceBaseUrl.trim());
      setTestResult(res);
      if (res.ok) {
        message.success(`连通成功！延迟 ${res.latencyMs}ms`);
      } else {
        message.error(res.error || `连通失败，HTTP ${res.statusCode}`);
      }
    } catch (e: any) {
      setTestResult({ ok: false, error: e.message || String(e) });
      message.error(`测试失败: ${e.message || e}`);
    } finally {
      setTestingUrl(false);
    }
  };

  return (
    <div className="agent-dev-page">
      <section className="agent-dev-hero">
        <div>
          <span className="agent-dev-kicker">Admin Agent Onboarding</span>
          <Title level={2}>开发者中心 / 管理员智能体接入</Title>
          <Paragraph>处理智能体工坊提交的接入申请，也可手工登记已有真实服务。系统编码由后台生成，不录入演示数据。</Paragraph>
        </div>
        <Tag color="cyan">仅 admin 可用</Tag>
      </section>

      <Card
        title={<Space><SafetyCertificateOutlined />智能体工坊接入申请</Space>}
        className="agent-dev-table-card"
        extra={<Button onClick={refresh} loading={loading}>刷新</Button>}
      >
        <Table
          rowKey="id"
          dataSource={reviews}
          locale={{ emptyText: <Empty description="暂无待处理接入申请" /> }}
          pagination={{ pageSize: 6 }}
          columns={[
            {
              title: '智能体',
              render: (_: any, row: any) => (
                <Space direction="vertical" size={0}>
                  <Text strong>{row.agent?.name}</Text>
                  <Text type="secondary">{row.agent?.agentCode}</Text>
                </Space>
              ),
            },
            {
              title: '提交人',
              render: (_: any, row: any) => row.submittedBy?.name || row.submittedBy?.username || '-',
            },
            {
              title: '当前环节',
              dataIndex: 'currentStep',
              render: (value: string) => {
                const labels: Record<string, string> = {
                  business_review: '业务审核',
                  security_review: '安全审核',
                  deploy_review: '投产审核',
                  approved: '审核通过',
                  listed: '已上架',
                  rejected: '已驳回',
                  changes_requested: '待修改',
                };
                return <Tag color={statusColor(value)}>{labels[value] || value}</Tag>;
              },
            },
            {
              title: '服务地址',
              render: (_: any, row: any) => <Text ellipsis={{ tooltip: row.agent?.serviceBaseUrl }}>{row.agent?.serviceBaseUrl || '-'}</Text>,
            },
            {
              title: '操作',
              width: 220,
              render: (_: any, row: any) => {
                const terminal = ['listed', 'rejected'].includes(row.currentStep);
                if (terminal) return <Text type="secondary">无需操作</Text>;
                return (
                  <Space>
                    <Button
                      size="small"
                      type="primary"
                      loading={reviewingId === row.id}
                      onClick={() => handleReviewDecision(row, row.currentStep === 'approved' ? 'list' : 'approve')}
                    >
                      {row.currentStep === 'approved' ? '上架' : '通过本环节'}
                    </Button>
                    <Button
                      size="small"
                      danger
                      icon={<CloseCircleOutlined />}
                      disabled={reviewingId === row.id}
                      onClick={() => handleReviewDecision(row, 'reject')}
                    >
                      驳回
                    </Button>
                  </Space>
                );
              },
            },
          ]}
        />
      </Card>

      <Card className="agent-dev-flow-card">
        <div className="agent-dev-wizard-shell">
          <aside className="agent-dev-step-rail">
            <Steps current={current} items={stepItems} direction="vertical" />
            <Alert
              className="agent-dev-alert"
              type="info"
              showIcon
              message="接入原则"
              description="只填业务人员能理解的内容；智能体编码由后台自动生成；服务调用路径按智坊固定协议约定。"
            />
          </aside>

          <main className="agent-dev-step-main">
            <Form
              form={form}
              layout="vertical"
              className="agent-dev-step-form"
              initialValues={{
                category: 'business',
                version: '1.0.0',
                pricingMode: 'free',
                inputTypes: ['text'],
                outputTypes: ['text', 'html', 'json'],
                streaming: 'false',
                permissions: [],
              }}
              onValuesChange={handleFormChange}
            >
              {current === 0 && (
                <div className="agent-dev-step-page">
                  <div className="agent-dev-page-title">
                    <span>01</span>
                    <div>
                      <Title level={3}>填写智能体基础信息</Title>
                      <Paragraph>只描述这个智能体是谁、做什么、属于哪个业务场景。唯一编码不在这里填，提交后后台自动生成 agent_ 开头的 UUID。</Paragraph>
                    </div>
                  </div>

                  <div className="agent-dev-big-form">
                    <Form.Item name="name" label="智能体名称" rules={[{ required: true, message: '请输入智能体名称' }]}>
                      <Input size="large" placeholder="例如：合同审查智能体、供应商风险分析智能体" />
                    </Form.Item>

                    <Form.Item name="description" label="智能体说明" rules={[{ required: true, message: '请输入真实能力说明' }]}>
                      <Input.TextArea rows={6} placeholder="说明它能处理什么业务、输出什么结果、适用边界是什么。不要填写演示内容。" />
                    </Form.Item>

                    <div className="agent-dev-form-grid">
                      <Form.Item name="category" label="业务分类" rules={[{ required: true }]}>
                        <Select size="large" options={[{ value: 'business', label: '业务类' }, { value: 'office', label: '办公类' }, { value: 'tech', label: '技术类' }]} />
                      </Form.Item>
                      <Form.Item name="subCategory" label="细分场景">
                        <Input size="large" placeholder="例如：法务 / 财务 / 运维 / 项目管理" />
                      </Form.Item>
                      <Form.Item name="version" label="版本号" rules={[{ required: true, message: '请输入版本号' }]}>
                        <Input size="large" />
                      </Form.Item>
                      <Form.Item name="pricingMode" label="计费方式">
                        <Select size="large" options={[{ value: 'free', label: '免费' }, { value: 'paid', label: '付费' }, { value: 'quota', label: '配额' }, { value: 'custom', label: '定制' }]} />
                      </Form.Item>
                    </div>

                    <Form.Item name="tags" label="标签">
                      <Input size="large" placeholder="多个标签用逗号或空格分隔" />
                    </Form.Item>
                  </div>
                </div>
              )}

              {current === 1 && (
                <div className="agent-dev-step-page">
                  <div className="agent-dev-page-title">
                    <span>02</span>
                    <div>
                      <Title level={3}>填写真实服务 URL</Title>
                      <Paragraph>管理员只需要填写智能体服务的根地址。健康检查和消息调用路径由智坊协议固定，不再让用户手填。</Paragraph>
                    </div>
                  </div>

                  <div className="agent-dev-url-card">
                    <Form.Item name="serviceBaseUrl" label="真实服务 URL" rules={[{ required: true, message: '请输入真实智能体服务 URL' }, { pattern: /^https?:\/\/[^\s/$.?#].[^\s]*$/, message: '请输入合法 URL，例如 https://agent.example.com' }]}>
                      <Input size="large" placeholder="https://agent.example.com" />
                    </Form.Item>

                    <Form.Item
                      name="serviceToken"
                      label="调用鉴权密钥（与智能体侧约定的同一串）"
                      extra="平台每次调用该智能体时，会用这串密钥用 HS256 签发 JWT，放在 Authorization: Bearer 头里发给智能体验签。请与智能体开发方约定同一串密钥后填入；留空则平台不发鉴权头（仅适用于智能体未开启鉴权的情况）。"
                    >
                      <Input.Password size="large" placeholder="例如 sufu-zhejiang-agent-2026-x8f2k... 建议 32 位以上随机串" autoComplete="new-password" />
                    </Form.Item>

                    <div style={{ marginBottom: 16 }}>
                      <Button
                        type="default"
                        loading={testingUrl}
                        onClick={handleTestConnectivity}
                        icon={<SendOutlined />}
                      >
                        测试连通性
                      </Button>
                      {testResult && (
                        <Tag
                          color={testResult.ok ? 'green' : 'red'}
                          style={{ marginLeft: 12, fontSize: 14, lineHeight: '28px' }}
                        >
                          {testResult.ok ? `✓ 连通成功 · ${testResult.latencyMs}ms` : '✗ 未能连通'}
                        </Tag>
                      )}
                      {testResult && !testResult.ok && (
                        <div style={{ marginTop: 8 }}>
                          <Text type="danger">{testResult.error || `HTTP ${testResult.statusCode}`}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 12 }}>实际调用：{testResult.healthUrl}</Text>
                        </div>
                      )}
                    </div>

                    <div className="agent-dev-protocol-box">
                      <Text strong>智坊固定协议路径</Text>
                      <p>健康检查：<code>{FIXED_AGENT_ENDPOINTS.health}</code></p>
                      <p>消息调用：<code>{FIXED_AGENT_ENDPOINTS.message}</code></p>
                      <small>系统提交时会把根地址和固定路径一起保存到 Manifest，页面不再要求用户理解或填写这些技术路径。</small>
                    </div>
                  </div>
                </div>
              )}

              {current === 2 && (
                <div className="agent-dev-step-page">
                  <div className="agent-dev-page-title">
                    <span>03</span>
                    <div>
                      <Title level={3}>声明能力与权限</Title>
                      <Paragraph>选择这个智能体支持的输入、输出类型。输出类型可以多选，比如同时支持文本、HTML 和 JSON。</Paragraph>
                    </div>
                  </div>

                  <div className="agent-dev-choice-section">
                    <Form.Item name="inputTypes" label="支持的输入类型" rules={[{ required: true, message: '请选择至少一种输入类型' }]}>
                      <Checkbox.Group options={inputTypeOptions} />
                    </Form.Item>
                    <Form.Item name="outputTypes" label="支持的输出类型" rules={[{ required: true, message: '请选择至少一种输出类型' }]}>
                      <Checkbox.Group options={outputTypeOptions} />
                    </Form.Item>
                    <div className="agent-dev-form-grid">
                      <Form.Item name="streaming" label="是否支持流式输出">
                        <Select size="large" options={[{ value: 'false', label: '否' }, { value: 'true', label: '是' }]} />
                      </Form.Item>
                    </div>
                    <Form.Item name="permissions" label="需要申请的权限">
                      <Checkbox.Group options={permissionOptions} />
                    </Form.Item>
                  </div>
                </div>
              )}

              {current === 3 && (
                <div className="agent-dev-step-page">
                  <Result icon={<CheckCircleOutlined />} title="确认接入" subTitle="请核对下面的人类可读摘要。确认后后台会生成 agent_ 开头的唯一编码，并将该真实智能体直接上架。" />
                  <div className="agent-dev-readable-summary">
                    <SummaryRow label="智能体名称">{manifest.name}</SummaryRow>
                    <SummaryRow label="智能体说明">{manifest.description}</SummaryRow>
                    <SummaryRow label="固定健康检查">/devforge-agent/v1/health</SummaryRow>
                    <SummaryRow label="固定消息调用">/devforge-agent/v1/messages</SummaryRow>
                    <SummaryRow label="输入类型">{manifest.inputModes?.length ? (manifest.inputModes as string[]).map(v => inputTypeOptions.find(o => o.value === v)?.label || v).join('、') : '未填写'}</SummaryRow>
                    <SummaryRow label="输出类型">{manifest.outputModes?.length ? (manifest.outputModes as string[]).map(v => outputTypeOptions.find(o => o.value === v)?.label || v).join('、') : '未填写'}</SummaryRow>
                    <SummaryRow label="权限">{(manifest.permissions || []).map((p: any) => p.name || p.code).join('、') || '无额外权限'}</SummaryRow>
                  </div>
                  <Alert className="agent-dev-final-note" type="warning" showIcon message="不再展示黑底 JSON" description="系统仍会在后台保存结构化 Manifest，但这里改成业务摘要，避免管理员看不懂技术 JSON。" />
                </div>
              )}
            </Form>

            <div className="agent-dev-actions">
              <Button size="large" icon={<LeftOutlined />} disabled={current === 0 || submitting} onClick={() => setCurrent(current - 1)}>上一步</Button>
              {current < stepItems.length - 1 ? (
                <Button size="large" type="primary" icon={<RightOutlined />} onClick={next}>下一步</Button>
              ) : (
                <Button size="large" type="primary" icon={<SendOutlined />} loading={submitting} onClick={submit}>确认接入并上架</Button>
              )}
            </div>
          </main>
        </div>
      </Card>

      <Card title="已接入智能体（真实数据库记录）" className="agent-dev-table-card" extra={<Button onClick={refresh} loading={loading}>刷新</Button>}>
        <Table
          rowKey="id"
          dataSource={agents}
          locale={{ emptyText: <Empty description="暂无管理员接入的智能体" /> }}
          pagination={{ pageSize: 8 }}
          columns={[
            { title: '智能体', dataIndex: 'name', render: (_: any, row: any) => <Space direction="vertical" size={0}><Text strong>{row.name}</Text><Text type="secondary">{row.agentCode}</Text></Space> },
            { title: '状态', dataIndex: 'status', render: (v: string) => <Tag color={statusColor(v)}>{v}</Tag> },
            { title: '版本', dataIndex: 'version' },
            { title: '服务地址', dataIndex: 'serviceBaseUrl', ellipsis: true },
          ]}
        />
      </Card>
    </div>
  );
}
